import {
  ActionIcon,
  Button,
  Card,
  Checkbox,
  CheckboxGroup,
  ColorInput,
  Grid,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconBolt, IconDeviceFloppy, IconFileText, IconPalette, IconTarget, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import EventSelector from '../EventSelector';

const BUTTON_STYLES = [
  { value: 'pill', label: 'Pill' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'square', label: 'Square' },
];

const POSITIONS = [
  { value: 'bottomSheet', label: 'Bottom Sheet' },
  { value: 'modal', label: 'Modal' },
];

const SHOW_POLICIES = [
  { value: 'untilAnswered', label: 'Until Answered' },
  { value: 'onceEver', label: 'Once Ever' },
  { value: 'recurring', label: 'Recurring' },
];

const PLATFORMS = ['ios', 'android', 'web'];

const NPSSurveyForm = ({ npsSurvey, saveNpsSurvey, deleteNpsSurvey, cancel, loading }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageOptions, setPageOptions] = useState([]);
  const [form, setForm] = useState(() => ({
    name: '',
    status: 'draft',
    question: 'How likely are you to recommend us to a friend or colleague?',
    scaleLowLabel: 'Not at all likely',
    scaleHighLabel: 'Extremely likely',
    followUpPromoter: 'What do you love most?',
    followUpPassive: 'What could we do better?',
    followUpDetractor: "What's the main reason for your score?",
    followUpRequired: false,
    submitLabel: 'Submit',
    skipLabel: 'Skip',
    thankYouPromoter: "Thank you! We're glad you love it.",
    thankYouPassive: 'Thank you for your feedback.',
    thankYouDetractor: "Thank you. We'll work on it.",
    appearance: {
      primaryColor: '#6C3FC4',
      backgroundColor: '#FFFFFF',
      textColor: '#1A1A1A',
      buttonStyle: 'pill',
      position: 'bottomSheet',
      logoUrl: '',
      dark: null,
    },
    triggers: [{ type: 'screenView', _id: crypto.randomUUID() }],
    triggerDelaySeconds: 2,
    showPolicy: 'untilAnswered',
    cooldownDays: 90,
    cancelOnEvents: [],
    platforms: [],
    appVersionMin: '',
    appVersionMax: '',
    minDaysSinceFirstContact: null,
    startDate: null,
    endDate: null,
    ...npsSurvey,
  }));

  const [darkModeEnabled, setDarkModeEnabled] = useState(!!npsSurvey?.appearance?.dark);
  const [nameError, setNameError] = useState('');
  const [logoUrlError, setLogoUrlError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [activeTab, setActiveTab] = useState('content');

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages`)
      .then(({ pages }) => setPageOptions(pages.map((p) => ({ value: p.name, label: p.name }))))
      .catch(() => {});
  }, [accountId, domainId, fetch]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const setAppearance = (field, value) => setForm((f) => ({ ...f, appearance: { ...f.appearance, [field]: value } }));
  const setDarkAppearance = (field, value) =>
    setForm((f) => ({
      ...f,
      appearance: { ...f.appearance, dark: { ...(f.appearance?.dark || {}), [field]: value } },
    }));

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setNameError('Survey name is required.');
      return;
    }
    setNameError('');
    if (form.appearance?.logoUrl && !form.appearance.logoUrl.startsWith('https://')) {
      setLogoUrlError('URL must start with https://');
      setActiveTab('appearance');
      return;
    }
    setLogoUrlError('');
    if (form.startDate && form.endDate && form.endDate <= form.startDate) {
      setEndDateError('End date must be after start date.');
      setActiveTab('targeting');
      return;
    }
    setEndDateError('');
    const payload = { ...form };
    if (!darkModeEnabled) {
      payload.appearance = { ...payload.appearance, dark: null };
    }
    if (payload.appearance?.logoUrl === '') {
      payload.appearance.logoUrl = null;
    }
    saveNpsSurvey(payload);
  };

  return (
    <>
      <Card withBorder>
        <Card.Section inheritPadding py="sm">
          <Group align="flex-end" mb="md" wrap="nowrap">
            <TextInput
              label="Survey name"
              value={form.name}
              onChange={(e) => {
                set('name', e.target.value);
                if (e.target.value.trim()) setNameError('');
              }}
              required
              error={nameError || undefined}
              style={{ flex: 1 }}
            />
            <Select
              label="Status"
              data={[
                { value: 'draft', label: 'Draft' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              value={form.status}
              onChange={(v) => set('status', v)}
              w={140}
            />
          </Group>
        </Card.Section>

        <Tabs value={activeTab} onChange={setActiveTab} keepMounted>
          <Tabs.List>
            <Tabs.Tab value="content" leftSection={<IconFileText size={14} />}>
              Content
            </Tabs.Tab>
            <Tabs.Tab value="appearance" leftSection={<IconPalette size={14} />}>
              Appearance
            </Tabs.Tab>
            <Tabs.Tab value="targeting" leftSection={<IconTarget size={14} />}>
              Targeting
            </Tabs.Tab>
            <Tabs.Tab value="triggers" leftSection={<IconBolt size={14} />}>
              Triggers
            </Tabs.Tab>
          </Tabs.List>

          {/* Content Tab */}
          <Tabs.Panel value="content" pt="md">
            <Stack>
              <Textarea
                label="Question"
                value={form.question}
                onChange={(e) => set('question', e.target.value)}
                autosize
                minRows={2}
              />
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Scale low label"
                    value={form.scaleLowLabel}
                    onChange={(e) => set('scaleLowLabel', e.target.value)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Scale high label"
                    value={form.scaleHighLabel}
                    onChange={(e) => set('scaleHighLabel', e.target.value)}
                  />
                </Grid.Col>
              </Grid>

              <Text fw={500} mt="sm">
                Follow-up questions
              </Text>
              <TextInput
                label="Promoters (9–10)"
                value={form.followUpPromoter || ''}
                onChange={(e) => set('followUpPromoter', e.target.value)}
              />
              <TextInput
                label="Passives (7–8)"
                value={form.followUpPassive || ''}
                onChange={(e) => set('followUpPassive', e.target.value)}
              />
              <TextInput
                label="Detractors (0–6)"
                value={form.followUpDetractor || ''}
                onChange={(e) => set('followUpDetractor', e.target.value)}
              />
              <Switch
                label="Follow-up comment is required"
                checked={form.followUpRequired}
                onChange={(e) => set('followUpRequired', e.currentTarget.checked)}
              />

              <Text fw={500} mt="sm">
                Labels
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Submit button"
                    value={form.submitLabel}
                    onChange={(e) => set('submitLabel', e.target.value)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Skip button (leave empty to hide)"
                    value={form.skipLabel || ''}
                    onChange={(e) => set('skipLabel', e.target.value || null)}
                  />
                </Grid.Col>
              </Grid>

              <Text fw={500} mt="sm">
                Thank-you messages
              </Text>
              <TextInput
                label="Promoters (9–10)"
                value={form.thankYouPromoter || ''}
                onChange={(e) => set('thankYouPromoter', e.target.value)}
              />
              <TextInput
                label="Passives (7–8)"
                value={form.thankYouPassive || ''}
                onChange={(e) => set('thankYouPassive', e.target.value)}
              />
              <TextInput
                label="Detractors (0–6)"
                value={form.thankYouDetractor || ''}
                onChange={(e) => set('thankYouDetractor', e.target.value)}
              />
            </Stack>
          </Tabs.Panel>

          {/* Appearance Tab */}
          <Tabs.Panel value="appearance" pt="md">
            <Stack>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <ColorInput
                    label="Primary color"
                    format="hex"
                    value={form.appearance?.primaryColor || ''}
                    onChange={(v) => setAppearance('primaryColor', v)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <ColorInput
                    label="Background color"
                    format="hex"
                    value={form.appearance?.backgroundColor || ''}
                    onChange={(v) => setAppearance('backgroundColor', v)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <ColorInput
                    label="Text color"
                    format="hex"
                    value={form.appearance?.textColor || ''}
                    onChange={(v) => setAppearance('textColor', v)}
                  />
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Select
                    label="Button style"
                    data={BUTTON_STYLES}
                    value={form.appearance?.buttonStyle || 'pill'}
                    onChange={(v) => setAppearance('buttonStyle', v)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Select
                    label="Position"
                    data={POSITIONS}
                    value={form.appearance?.position || 'bottomSheet'}
                    onChange={(v) => setAppearance('position', v)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="Logo URL (optional)"
                    value={form.appearance?.logoUrl || ''}
                    onChange={(e) => {
                      setAppearance('logoUrl', e.target.value);
                      if (!e.target.value || e.target.value.startsWith('https://')) setLogoUrlError('');
                    }}
                    error={
                      logoUrlError ||
                      (form.appearance?.logoUrl && !form.appearance.logoUrl.startsWith('https://')
                        ? 'URL must start with https://'
                        : undefined)
                    }
                  />
                </Grid.Col>
              </Grid>

              <Switch
                label="Enable dark mode colors"
                checked={darkModeEnabled}
                onChange={(e) => setDarkModeEnabled(e.currentTarget.checked)}
                mt="sm"
              />
              {darkModeEnabled && (
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <ColorInput
                      label="Dark primary color"
                      format="hex"
                      value={form.appearance?.dark?.primaryColor || ''}
                      onChange={(v) => setDarkAppearance('primaryColor', v)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <ColorInput
                      label="Dark background color"
                      format="hex"
                      value={form.appearance?.dark?.backgroundColor || ''}
                      onChange={(v) => setDarkAppearance('backgroundColor', v)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <ColorInput
                      label="Dark text color"
                      format="hex"
                      value={form.appearance?.dark?.textColor || ''}
                      onChange={(v) => setDarkAppearance('textColor', v)}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Targeting Tab */}
          <Tabs.Panel value="targeting" pt="md">
            <Stack>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <DatePickerInput
                    label="Start date"
                    clearable
                    // Mantine v9 DatePickerInput emits/accepts a 'YYYY-MM-DD'
                    // string, not a Date. Slicing the stored ISO value feeds it
                    // a bare date; the previous v.getFullYear() call threw on the
                    // string so the value never persisted.
                    value={form.startDate ? form.startDate.slice(0, 10) : null}
                    onChange={(v) => set('startDate', v ? `${v}T00:00:00` : null)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <DatePickerInput
                    label="End date (leave empty for no end)"
                    clearable
                    // Mantine v9 emits/accepts a 'YYYY-MM-DD' string (see Start
                    // date above). Preserve the end-of-day semantics the backend
                    // stores by appending T23:59:59.
                    value={form.endDate ? form.endDate.slice(0, 10) : null}
                    onChange={(v) => {
                      set('endDate', v ? `${v}T23:59:59` : null);
                      setEndDateError('');
                    }}
                    error={endDateError || undefined}
                  />
                </Grid.Col>
              </Grid>

              <CheckboxGroup
                label="Platforms (leave all unchecked to target all)"
                value={form.platforms || []}
                onChange={(v) => set('platforms', v)}
              >
                <Group mt="xs">
                  {PLATFORMS.map((p) => (
                    <Checkbox key={p} value={p} label={p.charAt(0).toUpperCase() + p.slice(1)} />
                  ))}
                </Group>
              </CheckboxGroup>

              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Min app"
                    placeholder="e.g. 2.0.0"
                    value={form.appVersionMin || ''}
                    onChange={(e) => set('appVersionMin', e.target.value || null)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Max app"
                    placeholder="e.g. 5.0.0"
                    value={form.appVersionMax || ''}
                    onChange={(e) => set('appVersionMax', e.target.value || null)}
                  />
                </Grid.Col>
              </Grid>

              <NumberInput
                label="Min days since first contact"
                description="Don't show to users who first visited less than N days ago"
                value={form.minDaysSinceFirstContact ?? ''}
                onChange={(v) => set('minDaysSinceFirstContact', v === '' ? null : v)}
                min={0}
                w={260}
              />
            </Stack>
          </Tabs.Panel>

          {/* Triggers Tab */}
          <Tabs.Panel value="triggers" pt="md">
            <Stack>
              <Text size="sm" c="dimmed">
                Add one or more triggers. The survey will show when any trigger fires (OR logic).
              </Text>

              <TriggersEditor
                triggers={form.triggers || []}
                onChange={(v) => set('triggers', v)}
                pageOptions={pageOptions}
              />

              <Grid>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <NumberInput
                    label="Trigger delay (seconds)"
                    description="Wait before showing the overlay"
                    value={form.triggerDelaySeconds ?? 2}
                    onChange={(v) => set('triggerDelaySeconds', v)}
                    min={0}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Select
                    label="Show policy"
                    description="How often to show to the same user"
                    data={SHOW_POLICIES}
                    value={form.showPolicy}
                    onChange={(v) => set('showPolicy', v)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <NumberInput
                    label="Cooldown days"
                    description="Minimum gap between showings"
                    value={form.cooldownDays ?? 90}
                    onChange={(v) => set('cooldownDays', v)}
                    min={1}
                  />
                </Grid.Col>
              </Grid>

              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Cancel on events
                </Text>
                <Text size="xs" c="dimmed">
                  Cancel a pending NPS if any of these events fire
                </Text>
                <EventSelector
                  isMulti
                  actions={form.cancelOnEvents || []}
                  setActions={(v) => set('cancelOnEvents', v)}
                />
              </Stack>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Card>

      <Group justify="flex-end" gap="xs" mt="md">
        <Button variant="default" onClick={cancel} disabled={loading}>
          Cancel
        </Button>
        {deleteNpsSurvey && (
          <Button color="red" variant="light" onClick={() => setConfirmDelete(true)} disabled={loading}>
            <IconTrash size={14} /> Delete
          </Button>
        )}
        <Button onClick={handleSubmit} loading={loading}>
          <IconDeviceFloppy size={14} /> Save
        </Button>
      </Group>

      {deleteNpsSurvey && (
        <Modal opened={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete NPS Survey">
          <Text mb="md">Are you sure you want to delete &quot;{form.name}&quot;? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={deleteNpsSurvey} loading={loading}>
              Delete
            </Button>
          </Group>
        </Modal>
      )}
    </>
  );
};

const TRIGGER_TYPES = [
  { value: 'screenView', label: 'Screen / Page View' },
  { value: 'customEvent', label: 'Custom Event' },
  { value: 'sessionCount', label: 'Session Count' },
];

const TriggersEditor = ({ triggers, onChange, pageOptions }) => {
  const add = () => onChange([...triggers, { type: 'screenView', _id: crypto.randomUUID() }]);
  const remove = (i) => onChange(triggers.filter((_, idx) => idx !== i));
  const update = (i, field, value) => {
    const next = triggers.map((t, idx) => (idx === i ? { ...t, [field]: value } : t));
    onChange(next);
  };

  return (
    <Stack gap="xs">
      {triggers.map((trigger, i) => (
        <Card key={trigger._id || i} withBorder p="sm">
          <Grid align="flex-end">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Select
                label="Trigger type"
                data={TRIGGER_TYPES}
                value={trigger.type}
                onChange={(v) => update(i, 'type', v)}
              />
            </Grid.Col>
            {trigger.type === 'screenView' && (
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <MultiSelect
                  label="Limit to screens (leave empty for all)"
                  placeholder="All screens"
                  data={pageOptions}
                  value={trigger.screenNames || []}
                  onChange={(v) => update(i, 'screenNames', v)}
                  searchable
                  clearable
                />
              </Grid.Col>
            )}
            {trigger.type === 'customEvent' && (
              <Grid.Col span={{ base: 12, sm: 5 }}>
                <Text size="sm" fw={500} mb={4}>
                  Event name
                </Text>
                <EventSelector
                  isMulti={false}
                  actions={trigger.eventName ? [trigger.eventName] : []}
                  setActions={(v) => update(i, 'eventName', v[0] || '')}
                />
              </Grid.Col>
            )}
            {trigger.type === 'sessionCount' && (
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <NumberInput
                  label="Min sessions"
                  value={trigger.minSessions || 1}
                  onChange={(v) => update(i, 'minSessions', v)}
                  min={1}
                />
              </Grid.Col>
            )}
            <Grid.Col span="content">
              <ActionIcon variant="outline" color="red" size="lg" onClick={() => remove(i)} aria-label="Remove trigger">
                <IconTrash size={16} />
              </ActionIcon>
            </Grid.Col>
          </Grid>
        </Card>
      ))}
      <Button variant="light" size="xs" onClick={add} w="fit-content">
        + Add trigger
      </Button>
    </Stack>
  );
};

export default NPSSurveyForm;
